import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Badge,
  FormControlLabel,
} from '@mui/material';
import {
  ImageOutlined as PictureIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import axios from '../../../../../../api/axios';

const PICTURE_TYPES = [
  { id: 'living_room', picture_type: 'Living Room', title: 'Living Room', description: 'Pictures of the living room area', alwaysVisible: true },
  { id: 'kitchen', picture_type: 'Kitchen', title: 'Kitchen', description: 'Pictures of the kitchen area', alwaysVisible: true },
  { id: 'bedroom', picture_type: 'Bedroom', title: 'Bedroom', description: 'Pictures of the bedroom(s)', alwaysVisible: true },
  { id: 'bathroom', picture_type: 'Bathroom', title: 'Bathroom', description: 'Pictures of the bathroom(s)', alwaysVisible: true },
  { id: 'entrance', picture_type: 'Entrance', title: 'Entrance', description: 'Pictures of the main entry to the unit/property', alwaysVisible: true },
  { id: 'exterior', picture_type: 'Exterior', title: 'Exterior', description: 'Pictures of the property exterior', alwaysVisible: true },
  { id: 'balcony', picture_type: 'Balcony', title: 'Balcony', description: 'Pictures of the balcony or patio', alwaysVisible: false },
  { id: 'storage', picture_type: 'Storage', title: 'Storage', description: 'Pictures of storage areas like closets or sheds', alwaysVisible: false },
  { id: 'garage', picture_type: 'Garage', title: 'Garage', description: 'Pictures of the garage or parking area', alwaysVisible: false },
  { id: 'common_area', picture_type: 'Common Area', title: 'Common Area', description: 'Pictures of shared building areas', alwaysVisible: false },
  { id: 'damage_report', picture_type: 'Damage Report', title: 'Damage Report', description: 'Pictures documenting property damage', alwaysVisible: false },
];

const getSignedUrl = async ({ key }) => {
  try {
    const response = await axios.post('/v2/master_sheet/archive/signed-url', { key });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching signed URL:', error);
    throw new Error('Failed to fetch signed URL');
  }
};

const getPictureAvailability = (picture) => {
  if (!picture) return false;
  return (
    (picture.key && picture.key !== '') ||
    (picture.url && picture.url !== '' && picture.url !== 'upload_requested')
  );
};

const StaticPictureCard = ({ pictureType, pictures, onPreview }) => {
  const hasPictures = pictures && pictures.length > 0;
  const pictureCount = pictures[0]?.previous_versions?.length
    ? pictures[0].previous_versions.length + 1
    : hasPictures
      ? 1
      : 0;
  const isVisible = pictureType.alwaysVisible || hasPictures;

  if (!isVisible) return null;

  return (
    <Grid item xs={12} sm={6} md={6} key={pictureType.id}>
      <Card
        variant="outlined"
        sx={{
          border: 2,
          borderColor: hasPictures ? 'success.main' : 'error.main',
          backgroundColor: hasPictures ? 'rgb(225 255 227)' : 'rgb(255 233 233)',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
          minWidth: 150,
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
              {hasPictures ? (
                <CheckIcon sx={{ color: 'success.main', mr: 1.5, fontSize: 20 }} />
              ) : (
                <ErrorIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  noWrap
                  title={pictureType.title}
                >
                  {pictureType.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {pictureType.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
              <Tooltip title={`Preview ${pictureType.title} Pictures`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onPreview(pictureType, pictures)}
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                >
                  <Badge
                    badgeContent={pictureCount}
                    color={hasPictures ? 'success' : 'error'}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <PreviewIcon fontSize="small" sx={{ color: hasPictures ? 'primary.main' : 'error.main' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const PictureTypePreviewDialog = ({ open, onClose, pictureType, pictures }) => {
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [showLatest, setShowLatest] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [signedUrls, setSignedUrls] = useState(new Map());

  // Fetch signed URLs for all pictures when dialog opens
  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!open || !pictures || pictures.length === 0) return;

      const urlMap = new Map();

      try {
        // Fetch signed URL for latest picture
        if (pictures[0] && pictures[0].key) {
          const { signedUrl } = await getSignedUrl({ key: pictures[0].key });
          urlMap.set(pictures[0].key, signedUrl);
        }

        // Fetch signed URLs for previous versions
        if (pictures[0]?.previous_versions) {
          await Promise.all(
            pictures[0].previous_versions.map(async (version) => {
              if (version.key) {
                try {
                  const signedUrl = await getSignedUrl({ key: version.key });
                  urlMap.set(version.key, signedUrl);
                } catch (error) {
                  console.error(`Error fetching signed URL for version ${version.version_number}:`, error);
                }
              }
            })
          );
        }

        setSignedUrls(urlMap);
      } catch (error) {
        console.error('Error fetching signed URLs:', error);
        setPreviewError('Failed to load picture URLs. Please try again.');
      }
    };

    if (open) {
      fetchSignedUrls();
    } else {
      // Reset state when dialog closes
      setSignedUrls(new Map());
      setSelectedPicture(null);
      setShowLatest(true);
      setPreviewError(null);
    }
  }, [open, pictures]);

  // Set initial selected picture after signed URLs are fetched
  useEffect(() => {
    if (open && pictures && pictures.length > 0 && signedUrls.size > 0) {
      const latestPic = pictures[0];
      if (latestPic && latestPic.key && signedUrls.has(latestPic.key)) {
        setSelectedPicture({
          ...latestPic,
          signedUrl: signedUrls.get(latestPic.key),
        });
        setShowLatest(true);
        setPreviewError(null);
      }
    }
  }, [open, pictures, signedUrls]);

  const handlePictureSelect = async (picture, isLatest = false) => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      if (!picture.key) {
        throw new Error('Picture key not available');
      }

      // Get signed URL from cache or fetch it
      let signedUrl = signedUrls.get(picture.key);

      if (!signedUrl) {
        signedUrl = await getSignedUrl({ key: picture.key });
        setSignedUrls((prev) => new Map(prev).set(picture.key, signedUrl));
      }

      setSelectedPicture({
        ...picture,
        signedUrl,
      });
      setShowLatest(isLatest);
    } catch (error) {
      console.error('Error loading picture preview:', error);
      setPreviewError('Failed to load picture preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async (picture) => {
    if (typeof window === 'undefined') return;

    try {
      let downloadUrl;

      // Get the download URL
      if (picture.key) {
        try {
          downloadUrl = signedUrls.get(picture.key);
          if (!downloadUrl) {
            downloadUrl = await getSignedUrl({ key: picture.key });
            setSignedUrls((prev) => new Map(prev).set(picture.key, downloadUrl));
          }
        } catch (error) {
          console.error('Error getting signed URL for download:', error);
          downloadUrl = picture.url;
        }
      } else {
        downloadUrl = picture.url;
      }

      // Validate download URL
      if (!downloadUrl || downloadUrl === 'upload_requested') {
        throw new Error('Picture not available for download');
      }

      // Check for HTML content to avoid opening login/error pages
      try {
        const response = await fetch(downloadUrl, {
          method: 'HEAD',
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }

        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error('Server returned HTML instead of the file');
        }
      } catch (fetchError) {
        console.warn('HEAD request failed, proceeding with caution:', fetchError);
      }

      // Open the picture in a new tab
      window.open(downloadUrl.signedUrl, '_blank', 'noopener,noreferrer');

      console.log(`Opened picture in new tab: ${downloadUrl}`);
    } catch (error) {
      console.error(`Error opening picture:`, error);
      Swal.fire({
        title: 'Error',
        text: `Failed to open picture: ${error.message}`,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleDownloadAll = async () => {
    try {
      if (!pictures || pictures.length === 0) {
        Swal.fire('No Pictures', 'No pictures available to open.', 'info');
        return;
      }

      Swal.fire({
        title: 'Opening Pictures...',
        text: 'Please wait while we prepare your pictures.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let openCount = 0;
      let failedCount = 0;
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Open latest pictures
      for (const pic of pictures) {
        if (getPictureAvailability(pic)) {
          try {
            await handleDownload(pic);
            openCount++;
            await delay(1000);
          } catch (error) {
            console.error(`Failed to open picture:`, error);
            failedCount++;
          }
        }

        // Open previous versions
        if (pic.previous_versions && Array.isArray(pic.previous_versions)) {
          for (const version of pic.previous_versions) {
            if (getPictureAvailability(version)) {
              try {
                await handleDownload(version);
                openCount++;
                await delay(1000);
              } catch (error) {
                console.error(`Failed to open version ${version.version_number}:`, error);
                failedCount++;
              }
            }
          }
        }
      }

      Swal.close();

      if (openCount === 0 && failedCount === 0) {
        Swal.fire('No Pictures', 'No valid pictures were available to open.', 'info');
      } else if (openCount > 0 && failedCount === 0) {
        Swal.fire('Success', `${openCount} picture${openCount > 1 ? 's' : ''} opened successfully.`, 'success');
      } else if (openCount > 0 && failedCount > 0) {
        Swal.fire('Partial Success', `${openCount} picture${openCount > 1 ? 's' : ''} opened successfully. ${failedCount} failed.`, 'warning');
      } else {
        Swal.fire('Error', 'All pictures failed to open. Please check your network connection and try again.', 'error');
      }
    } catch (error) {
      Swal.close();
      console.error('Error in open all process:', error);
      Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
    }
  };

  const handleToggleLatest = () => {
    if (pictures && pictures.length > 0) {
      if (!showLatest) {
        handlePictureSelect(pictures[0], true);
      } else {
        setShowLatest(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{pictureType?.title || 'Pictures'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {pictures && pictures.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FeatherIcon icon="download" size="16" />}
              onClick={handleDownloadAll}
            >
              Download All
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <FeatherIcon icon="x" size="16" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {pictures && pictures.length > 0 ? (
          <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <CircularProgress />
                  <Typography variant="body2" ml={2}>
                    Loading preview...
                  </Typography>
                </Box>
              ) : previewError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Alert severity="error">{previewError}</Alert>
                </Box>
              ) : selectedPicture && selectedPicture.signedUrl ? (
                <img
                  src={selectedPicture.signedUrl}
                  alt={selectedPicture.picture_type}
                  style={{ width: '100%', height: '60vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Typography color="text.secondary">Picture preview not available.</Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Picture Versions
                </Typography>
                <FormControlLabel
                  control={<Switch checked={showLatest} onChange={handleToggleLatest} size="small" />}
                  label="Show Latest"
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Box>
              <Grid container spacing={1}>
                {pictures[0] && (
                  <Grid item xs={6} sm={6} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: getPictureAvailability(pictures[0]) ? 'success.main' : 'error.main',
                        backgroundColor:
                          selectedPicture === pictures[0] && showLatest
                            ? 'primary.light'
                            : getPictureAvailability(pictures[0])
                              ? 'rgb(225 255 227)'
                              : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePictureSelect(pictures[0], true)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {getPictureAvailability(pictures[0]) ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <PictureIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                noWrap
                                title="Latest Version"
                              >
                                Latest
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {pictures[0].comments || 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded:{' '}
                                {pictures[0].updated_at
                                  ? new Date(pictures[0].updated_at).toLocaleDateString()
                                  : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePictureSelect(pictures[0], true);
                                }}
                                disabled={!getPictureAvailability(pictures[0])}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(pictures[0]);
                                }}
                                disabled={!getPictureAvailability(pictures[0])}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {pictures[0]?.previous_versions?.map((version) => (
                  <Grid item xs={6} sm={6} md={4} key={version.version_number}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: getPictureAvailability(version) ? 'success.main' : 'error.main',
                        backgroundColor:
                          selectedPicture?.version_number === version.version_number && !showLatest
                            ? 'primary.light'
                            : getPictureAvailability(version)
                              ? 'rgb(225 255 227)'
                              : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePictureSelect(version, false)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {getPictureAvailability(version) ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <PictureIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={version.comments || 'No comment available'}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  noWrap
                                  title={`Version ${version.version_number}`}
                                >
                                  V{version.version_number}
                                </Typography>
                              </Tooltip>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {version.comments || 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded:{' '}
                                {version.updated_at ? new Date(version.updated_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePictureSelect(version, false);
                                }}
                                disabled={!getPictureAvailability(version)}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Picture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(version);
                                }}
                                disabled={!getPictureAvailability(version)}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, height: '70vh' }}>
            <PictureIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {pictureType?.title} pictures found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Upload pictures to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FeatherIcon icon="upload" size="16" />}
              onClick={onClose}
            >
              Upload First Picture
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Pictures = ({ pictures }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPictureType, setSelectedPictureType] = useState(null);
  const [selectedPictures, setSelectedPictures] = useState([]);

  const getPicturesByType = (typeId) => {
    const pictureType = PICTURE_TYPES.find((pt) => pt.id === typeId)?.picture_type;
    return pictures.filter(
      (pic) =>
        pic.picture_type === pictureType ||
        (pic.name &&
          pictureType &&
          pic.name.toLowerCase().trim().includes(pictureType.toLowerCase().trim()))
    );
  };

  const handlePreviewOpen = (pictureType, picturesOfType) => {
    setSelectedPictureType(pictureType);
    setSelectedPictures(picturesOfType || []);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedPictureType(null);
    setSelectedPictures([]);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 0, pt: 0 }}>
        {PICTURE_TYPES.map((pictureType) => {
          const picturesOfType = getPicturesByType(pictureType.id);
          const hasPictures = picturesOfType && picturesOfType.length > 0;

          if (pictureType.alwaysVisible || hasPictures) {
            return (
              <StaticPictureCard
                key={pictureType.id}
                pictureType={pictureType}
                pictures={picturesOfType}
                onPreview={handlePreviewOpen}
              />
            );
          }
          return null;
        })}
      </Grid>
      <PictureTypePreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        pictureType={selectedPictureType}
        pictures={selectedPictures}
      />
    </Box>
  );
};

const PicturesWrapper = ({ pictures }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDownloadAll = async () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to download all pictures?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, download',
      cancelButtonText: 'No, cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (pictures.length === 0) {
            Swal.fire('No Pictures', 'No pictures available for download.', 'info');
            return;
          }

          let downloadCount = 0;
          for (const pic of pictures) {
            try {
              let downloadUrl;

              if (pic.key) {
                try {
                  downloadUrl = await getSignedUrl({ key: pic.key });
                } catch (error) {
                  console.error('Error getting signed URL:', error);
                  downloadUrl = pic.url;
                }
              } else {
                downloadUrl = pic.url;
              }

              if (downloadUrl && downloadUrl !== 'upload_requested') {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = pic.name || pic.picture_type || `picture_${pic.created_at}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                downloadCount++;
                await new Promise((resolve) => setTimeout(resolve, 1000));
              }
            } catch (err) {
              console.error(`Error downloading picture ${pic.picture_type}:`, err);
            }

            if (pic.previous_versions) {
              for (const version of pic.previous_versions) {
                try {
                  let downloadUrl;

                  if (version.key) {
                    try {
                      downloadUrl = await getSignedUrl({ key: version.key });
                    } catch (error) {
                      console.error('Error getting signed URL for version:', error);
                      downloadUrl = version.url;
                    }
                  } else {
                    downloadUrl = version.url;
                  }

                  if (downloadUrl && downloadUrl !== 'upload_requested') {
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download =
                      version.name || version.picture_type || `picture_${version.version_number}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    downloadCount++;
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                  }
                } catch (err) {
                  console.error(`Error downloading version ${version.version_number}:`, err);
                }
              }
            }
          }

          if (downloadCount === 0) {
            Swal.fire('No Pictures', 'No valid pictures were available for download.', 'info');
          } else {
            Swal.fire(
              'Success',
              `${downloadCount} picture${downloadCount > 1 ? 's' : ''} downloaded successfully.`,
              'success'
            );
          }
        } catch (err) {
          console.error('Error downloading pictures:', err);
          Swal.fire('Error', 'Failed to download pictures.', 'error');
        }
      }
    });
  };

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton
          color="primary"
          outline
          className="fw-bold px-4 py-2 rounded-pill"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <FeatherIcon icon="image" className="me-2" size={16} />
          Pictures (
          {PICTURE_TYPES.filter(
            (pt) =>
              pt.alwaysVisible ||
              pictures.some(
                (pic) =>
                  pic.picture_type === pt.picture_type ||
                  (pic.name &&
                    pt.picture_type &&
                    pic.name.toLowerCase().trim().includes(pt.picture_type.toLowerCase().trim()))
              )
          ).length}
          )
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px', gap: 2 }}>
            <RButton
              color="secondary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleDownloadAll()}
            >
              <FeatherIcon icon="download" size="16" />
              Download All
            </RButton>
          </Box>
          <Pictures pictures={pictures} />
        </CardContent>
      </RCollapse>
    </RCard>
  );
};

PicturesWrapper.propTypes = {
  pictures: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      key: PropTypes.string,
      picture_type: PropTypes.string,
      comments: PropTypes.string,
      size: PropTypes.number,
      previous_versions: PropTypes.array,
      created_by: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
    })
  ).isRequired,
};

export { PicturesWrapper as Pictures };